/**
 * Expression-namespace vocabulary — the SINGLE place this validator writes down
 * what may appear at the head of a `#{…}` expression.
 *
 * Why this file exists
 * --------------------
 * Before it, the same fact was hand-typed in five places
 * (`CANONICAL_EXPR_PREFIXES`, `stage-runtime-expr`'s `RUNTIME_NAMESPACES`,
 * `stage-actionflow`'s `knownNamespaces`, `ontology-loader`'s
 * `RUNTIME_NS_FALLBACK`, `stage-semantic/variable-refs`' `KNOWN_NAMESPACES`)
 * and the five disagreed. Three of them omitted `local` — the FIRST member of
 * the closed canonical enum — which is why `bind-state-missing` fired on
 * `local.statusFilter` in blocks that had declared `statusFilter`.
 *
 * The rule for this file: **members are declared once**. A consumer that needs
 * more than the canonical enum composes the named groups below; it does not
 * retype names. Adding a name in one consumer and not another is exactly the
 * failure this module exists to make impossible.
 *
 * Browser-safety: this module does no runtime file IO — the canonical list
 * arrives as a static ESM JSON import, the same convention `ontology-loader.ts`
 * uses (Phase 003).
 */

// The canonical 9 are DERIVED, never typed here. `expression-prefix.json` is
// auto-generated from `cogo-kg-browser/fixtures/expression-prefix-v1.ttl`
// (`cogo:ExpressionPrefixCanonicalShape` / `sh:in`) by
// `cogo-kg-browser/scripts/generate-expression-prefix-json.ts`, and
// `cogo-schema/src/__tests__/expression-prefix-codegen.test.ts` holds it
// byte-identical to what the TTL produces. Importing it means this package
// holds ZERO independent statements of the canonical vocabulary.
import expressionPrefixOntology from "@cogo/schema/v4/definitions/expression-prefix.json" with { type: "json" }

interface OntologyPrefix {
  readonly name: string
  readonly semantics?: string
  readonly storage?: string
  readonly readOnly?: boolean
}

const ONTOLOGY_PREFIXES = (expressionPrefixOntology as { prefixes: OntologyPrefix[] }).prefixes

/**
 * Canonical prefixes: accepted, no violation. Closed enum, 9 members, in TTL
 * declaration order: local, event, props, item, _results, computed, appData,
 * env, route.
 */
export const CANONICAL_EXPR_PREFIXES: ReadonlySet<string> = new Set(
  ONTOLOGY_PREFIXES.map((p) => p.name),
)

/**
 * Canonical prefixes that name host-injected context for a capability slice
 * (`RequiredHostContextRequirement.root`). Derived from the canonical enum —
 * a name that leaves the TTL cannot remain here. Consumers import this set;
 * they do not retype `props` / `event` / `env` / `route`.
 */
const HOST_CONTEXT_PREFIX_NAMES = ["props", "event", "env", "route"] as const
export const HOST_CONTEXT_PREFIX_ROOTS: ReadonlySet<(typeof HOST_CONTEXT_PREFIX_NAMES)[number]> =
  new Set(
    HOST_CONTEXT_PREFIX_NAMES.filter((name): name is (typeof HOST_CONTEXT_PREFIX_NAMES)[number] =>
      CANONICAL_EXPR_PREFIXES.has(name),
    ),
  )

/**
 * Preserved second-tier: empty after Wave 10D (appData → canonical, _args
 * dropped — 0 corpus uses). Kept for back-compat of the `scanExpressionPrefixes`
 * return shape.
 */
export const PRESERVED_EXPR_PREFIXES: ReadonlySet<string> = new Set<string>([])

/**
 * Legacy prefixes: trigger the `expression-prefix-legacy` rule (error severity).
 * Map value → migration hint for LLM retry feedback.
 *
 * This object is the single machine-readable source for the legacy set; the
 * groups below derive from its keys rather than restating them.
 */
export const LEGACY_EXPR_PREFIXES: Record<string, string> = {
  state: "state → local",
  localState: "localState → local",
  response: "response → _results.__last (restApi result) — use a named step with resultTarget",
  ui: "ui → appData (or migrate to computed if reactive)",
  params: "params → route.params (route param) or props.<name> (block input)",
  page: "page → appData.<pageId> (page-scoped state lives under appData)",
}

// ── DECLARED EXTRAS ──────────────────────────────────────────────────────────
// Roots that are legal at the head of an expression but are NOT members of the
// canonical prefix enum. Each group states why it is not in the enum. Nothing
// may be added to a consumer's set without being added to one of these groups.

/**
 * Host-provided roots. These are supplied by the app/runtime host rather than
 * by the block's own declarations, so they are outside the *authoring* prefix
 * enum (which governs what a generator may emit as a data prefix) while still
 * resolving at runtime.
 *
 * - `app`, `auth`, `theme`  — host singletons injected by the runtime shell.
 * - `config`, `_config`     — build/host configuration objects.
 * - `_system`               — runtime-internal scope (engine bookkeeping).
 * - `block`                 — the enclosing block's own descriptor.
 */
export const HOST_NAMESPACES: readonly string[] = [
  "app",
  "auth",
  "theme",
  "config",
  "_config",
  "_system",
  "block",
]

/**
 * Globals the expression evaluator exposes. They are function/namespace objects,
 * not data prefixes, so the prefix enum deliberately does not list them — but a
 * reference rooted at one of them is legal and must not be reported as an
 * undeclared path.
 *
 * Source-of-truth for the accessor surface itself is
 * `cogo-kg-browser/fixtures/expression-accessors-v1.ttl` (Math.* lives there);
 * this list is only the set of legal *roots*.
 */
export const EVALUATOR_GLOBALS: readonly string[] = [
  "JSON",
  "Math",
  "Date",
  "String",
  "Number",
  "Array",
  "Object",
]

/**
 * Legacy prefixes, tolerated as *roots* but not as authoring vocabulary.
 *
 * Checked rather than assumed, and the check corrected the assumption: the
 * runtime read-alias layer (`cogo-runtime-v2/src/symbol-store.ts`
 * `LEGACY_READ_ALIAS`, feature flag `COGO_RUNTIME_LEGACY_PREFIX_ALIASES`,
 * default ON) aliases only TWO of these six — `state` and `localState`, both
 * onto `local`. `response`, `ui`, `params` and `page` are NOT resolved by it.
 * So "the runtime tolerates legacy on read" is not the reason to admit them.
 *
 * The reason to admit them is single-reporting: all six are already rejected at
 * error severity by exactly one rule, `expression-prefix-legacy`
 * (`stage-actionflow.ts:518`, `stage-runtime-expr.ts:88`,
 * `RULE_EXPRESSION_PREFIX_LEGACY`), which carries the migration hint. Listing
 * them here keeps the *namespace-unknown* family from emitting a second, less
 * useful error for the same character.
 *
 * Three of the five pre-fold sets admitted a subset of these, which is what
 * made the admission look accidental; it is intentional, but it is now stated
 * once and derived from `LEGACY_EXPR_PREFIXES`, so it cannot drift from the
 * rule that rejects them.
 *
 */
export const LEGACY_TOLERATED_NAMESPACES: readonly string[] = Object.keys(LEGACY_EXPR_PREFIXES)

/**
 * The composed vocabulary: every root that is legal at the head of an
 * expression. All four non-canonical consumer sets are now this set.
 */
export const KNOWN_EXPRESSION_ROOTS: ReadonlySet<string> = new Set<string>([
  ...CANONICAL_EXPR_PREFIXES,
  ...HOST_NAMESPACES,
  ...EVALUATOR_GLOBALS,
  ...LEGACY_TOLERATED_NAMESPACES,
])

const BARE_EXPRESSION_REFERENCE_ROOT = /^([A-Za-z_][A-Za-z0-9_]*)(?=\.|\[)/

/** True when a bare route value begins with a known expression root. */
export function isKnownBareExpressionReference(value: string): boolean {
  const root = BARE_EXPRESSION_REFERENCE_ROOT.exec(value)?.[1]
  return root !== undefined && KNOWN_EXPRESSION_ROOTS.has(root)
}

/**
 * The declared extras, keyed by group, for the derivation gate. A consumer set
 * may contain a name only if it is canonical or appears in one of these groups.
 */
export const DECLARED_EXTRAS: Readonly<Record<string, readonly string[]>> = {
  HOST_NAMESPACES,
  EVALUATOR_GLOBALS,
  LEGACY_TOLERATED_NAMESPACES,
}

// ── The declared local-state store ───────────────────────────────────────────

/**
 * Prefixes that address the block's declared local-state store
 * (`variables.localState` / `block.stateContract.localState`).
 *
 * `local.` is the canonical name for that store. `state.` and `localState.` are
 * aliases the runtime itself maps onto it (`LEGACY_READ_ALIAS`, checked above).
 * `ui.` is NOT a runtime alias — it is stripped here only because
 * `stage-contract`'s pre-fold `STATE_PREFIXES` stripped it, and this fold is not
 * the place to change that behaviour; it is carried forward deliberately and
 * flagged, not justified.
 *
 * A reference through any of these names a **key inside the store**, so a
 * declaration check must resolve the key, not the prefix. `stage-contract`
 * expressed this twice — a `STATE_PREFIXES` array AND a second, inline
 * `/^state\./` regex in the bind check that had drifted to a shorter list. Both
 * are now this one list, and the canonical `local.` joins them, which is what
 * stops `bind-state-missing` firing on a key the block did declare.
 */
export const LOCAL_STATE_PREFIXES: readonly string[] = ["local.", "state.", "localState.", "ui."]

/** The same set as bare roots (no trailing dot). */
export const LOCAL_STATE_PREFIX_ROOTS: ReadonlySet<string> = new Set(
  LOCAL_STATE_PREFIXES.map((p) => p.slice(0, -1)),
)

/**
 * WRITE-SIDE subset — the prefixes a **write target** may carry and still name
 * a declared key. Deliberately NOT `LOCAL_STATE_PREFIXES`.
 *
 * That list is a READ-side leniency: `stage-contract` tolerates the legacy
 * spellings on a bind because the pre-fold code did, and the fold carried that
 * forward rather than changing behaviour in passing. Reusing it on the write
 * side would be a different decision made silently — and it was: the first cut
 * of the ABox alias fix (2026-08-09) published all four, which stopped
 * `setState-target-undeclared` flagging `state.` / `localState.` / `ui.`
 * targets that BASE did flag, with nothing else catching them
 * (`expression-prefix-legacy` keys on `#{…}`, never on a bare `params.target`).
 * That is a safety-channel rule quietly loosened, so it is narrowed here.
 *
 * Why exactly `local.`: it is the sole CANONICAL prefix for the block-local
 * store. `@cogo/runtime-v2`'s `LEGACY_READ_ALIAS` maps `state`/`localState` to
 * `local` on READ only, and canonical policy is that writes normalise to the
 * canonical spelling — so a legacy-prefixed write target is not a spelling of
 * a declared symbol, it is a defect the validator should keep reporting.
 * `ui.` is not a runtime alias in either direction.
 */
export const CANONICAL_WRITE_TARGET_PREFIXES: readonly string[] = ["local."]

/** Strip a local-state store prefix from a reference, if it carries one. */
export function stripLocalStatePrefix(ref: string): string {
  for (const prefix of LOCAL_STATE_PREFIXES) {
    if (ref.startsWith(prefix)) return ref.slice(prefix.length)
  }
  return ref
}
